import { SaveOutlined } from '@ant-design/icons';
import { Col, Form, Input, message, Modal, Row, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, UNKNOWN_ERROR_MESSAGE, VALIDATE_REQUIRED, formatDisplayFullDate } from '@/config';
import Helmet from 'react-helmet';
import { SupportedLanguageType } from '@/constants';
import { PdfTemplateNameModel, PdfPagingResponse } from '@/__generated';
import { apiSdk } from '@/httpClient';
import CreateBtn from '@/components/app/CreateBtn';

const PAGE_TITLE = 'pdfTemplatesPage_Title';

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'name', label: 'pdfTemplatesPage_Name' }];

const PdfTemplateTab = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [pdfTemplates, setPdfTemplates] = useState<PdfTemplateNameModel[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const getEmailTemplateNames = () => {
    apiSdk.PdfTemplateService.getPdfTemplateNames()
      .then(response => {
        setPdfTemplates(response);
      })
      .catch(error => {
        message.error(t(UNKNOWN_ERROR_MESSAGE));
      });
  };

  const createEmailTemplate = values => {
    setLoading(true);
    apiSdk.PdfTemplateService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || UNKNOWN_ERROR_MESSAGE));
          }
        }
      })
      .catch(error => {
        message.error(t(UNKNOWN_ERROR_MESSAGE));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns: IColumnType<PdfPagingResponse>[] = React.useMemo(() => {
    return [
      {
        title: 'pdfTemplatesPage_Name',
        dataIndex: 'name',
        render: v => pdfTemplates?.find(x => x.key === v)?.name || v,
      },
      {
        title: 'pdfTemplatesPage_CultureCode',
        dataIndex: 'cultureCode',
      },
      {
        title: 'base.CreatedBy',
        dataIndex: 'createdBy',
      },
      {
        title: 'base.CreatedOn',
        dataIndex: 'createdOn',
        render: v => formatDisplayFullDate(v),
      },
      {
        title: 'base.UpdatedBy',
        dataIndex: 'updatedBy',
      },
      {
        title: 'base.UpdatedOn',
        dataIndex: 'updatedAt',
        render: v => formatDisplayFullDate(v),
      },
    ];
  }, [pdfTemplates]);

  const updateEmailTemplate = values => {
    setLoading(true);
    apiSdk.PdfTemplateService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getEmailTemplateNames();
  }, []);

  return (
    <div>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          filters={filters}
          tableRef={zTb}
          columns={columns}
          primaryKey="id"
          url="/commons/pdf-templates/get-pdf-templates"
          bordered
          order_by="Name"
          order_by_asc={true}
          allowEdit={true}
          searchBtnAlignLeft
          onEdit={record => {
            form.resetFields();
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue(record);
          }}
          allowDelete={true}
          delete_url="/commons/pdf-templates/delete"
          extraBtns={
            <CreateBtn
              onClick={() => {
                setIsNewForm(true);
                setFormVisible(true);
                form.resetFields();
              }}
            />
          }
        ></ZTable>
        <Modal
          className="nta-custom-form"
          open={formVisible}
          title={isNewForm ? t('pdfTemplatesPage_CreatePdfTemplate') : t('pdfTemplatesPage_UpdatePdfTemplate')}
          width={ModalWidth.extraLarge}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEmailTemplate(values);
                } else {
                  createEmailTemplate(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="vertical">
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={16}>
              <Col md={8}>
                <Form.Item name="name" label={t('pdfTemplatesPage_Name')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select placeholder={t('pdfTemplatesPage_Name')}>
                    {pdfTemplates.map(k => (
                      <Select.Option key={k.key} value={k.key}>
                        {k.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="cultureCode" label={t('emailTemplate_CultureCode')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select options={SupportedLanguageType.__getSelectOptions()} />
                </Form.Item>
              </Col>
              <Col md={16}>
                <Form.Item name="content" label={t('pdfTemplatesPage_Content')}>
                  <Input.TextArea rows={20} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};
export default PdfTemplateTab;
